import { Button } from '@radix-ui/themes'
import { useWallet } from '@solana/wallet-adapter-react'
import { AuthButton } from 'features/auth/components/auth-buttons'
import { getWalletAppOptions } from 'features/auth/utils/get-solana-connector-providers'
import type { SolanaConnectorProvider } from 'features/auth/utils/get-solana-connector-providers'
import { DataTestIDs } from 'shared/constants'
import { RadixText, XyroLoading } from 'shared/ui'
import { ProviderIcon } from './provider-icon'

interface Props {
  isBusy: boolean
  onPick: (provider: SolanaConnectorProvider) => void
  /** Button text for a wallet; its name by default. */
  getLabel?: (provider: SolanaConnectorProvider) => string
  buttonTestId: DataTestIDs
}

/**
 * The wallet apps a user can pick (FE-306): named wallets, installed or with
 * an "Install …" link, plus every other installed Solana wallet. Shared by
 * linking (Your wallets) and connecting (Connect wallet).
 */
export const WalletAppList: React.FC<Props> = ({
  isBusy,
  onPick,
  getLabel = provider => provider.name,
  buttonTestId
}) => {
  const { wallets } = useWallet()

  return (
    <>
      {getWalletAppOptions(wallets).map(({ provider, isInstalled, icon }) => {
        if (!isInstalled) {
          return provider.installUrl ?
              <Button
                key={provider.id}
                asChild
                variant='soft'
                color='gray'
                size='3'
                data-testid={DataTestIDs.linkWalletInstall}
              >
                <a
                  href={provider.installUrl}
                  target='_blank'
                  rel='noreferrer'
                >
                  Install {provider.name}
                </a>
              </Button>
            : null
        }

        return (
          <AuthButton
            key={provider.id}
            disabled={isBusy}
            onClick={() => onPick(provider)}
            dataTestID={buttonTestId}
          >
            <XyroLoading loading={isBusy}>
              <ProviderIcon
                provider={provider}
                adapterIcon={icon}
              />
              <RadixText>{getLabel(provider)}</RadixText>
            </XyroLoading>
          </AuthButton>
        )
      })}
    </>
  )
}
